import React, { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import InputWithChecks from "../../shared/InputWithChecks";
import Container from "../../shared/Container";
import PrimaryButton from "../../shared/PrimaryButton";
import SuccessButton from "../../shared/SuccessButton";
import BoxBorder from "../../shared/BoxBorder";
import LoadingIndicatorButton from "../../shared/LoadingIndicatorButton";
import SuccessMessage from "../../shared/SuccessMessage";
import { queryClient, addVacationMode, updateVacationMode } from "../../../util/admin/httpAdmin";

import plusIconWhite from "../../../assets/admin/icons/plus-white.svg";
import editIconWhite from "../../../assets/admin/icons/edit-white.svg";


const formatDateForInput = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
};


const VacationModeForm = ({ onSuccessClose, editMode = false, previousData = {} }) => {
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const reasonRef = useRef(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Kies de juiste mutation functie afhankelijk van editMode
  const mutationFn = editMode ? updateVacationMode : addVacationMode;

  const { mutate, isPending, isError, error } = useMutation({
    mutationFn,
    onSuccess: () => {
      setSuccessMessage(
        editMode ? "Vakantie modus succesvol bijgewerkt!" : "Vakantie modus succesvol toegevoegd!"
      );
      queryClient.invalidateQueries(["vacationModes"]); // Maak query voor vacationModes ongeldig
      setTimeout(() => {
        onSuccessClose(); // Sluit de modal na 2 seconden

        setTimeout(() => {
          setSuccessMessage("");
        }, 100);
      }, 500);
    },
  });

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const startDateValid = startDateRef.current?.validateInput();
    const endDateValid = endDateRef.current?.validateInput();
    const reasonValid = reasonRef.current?.validateInput();

    if (startDateValid && endDateValid && reasonValid) {
      const formData = {
        vacationModeStart: startDateRef.current.getValue(),
        vacationModeEnd: endDateRef.current.getValue(),
        vacationModeReason: reasonRef.current.getValue(),
      };

      if (editMode && previousData?._id) {
        formData._id = previousData._id; // Voeg het ID toe voor updates
      }

      mutate(formData);
    }
  };

  return (
    <section>
      <Container>
        {successMessage ? (
          <SuccessMessage title="Succes!" description={successMessage} />
        ) : (
          <BoxBorder extraClasses="mxw-1000 m-a g-20">
            <h2>{editMode ? "Vakantie Modus Bewerken" : "Vakantie Modus Toevoegen"}</h2>
            <form className="g-20">
              <div className="g-1-1 a-i-s gap-1">
                <InputWithChecks
                  ref={startDateRef}
                  label="Vakantie Begin Datum"
                  inputType="date"
                  validate={(value) => {
                    if (!value) return "Begindatum is verplicht.";
                    if (new Date(value) < new Date().setHours(0, 0, 0, 0)) return "Begindatum moet vandaag of later zijn.";
                    return "";
                  }}
                  placeholder="Kies een begin datum"
                  value={editMode ? formatDateForInput(previousData.vacationModeStart) : ""}
                />
       
                <InputWithChecks
                  ref={endDateRef}
                  label="Vakantie Eind Datum"
                  inputType="date"
                  validate={(value) => {
                    if (!value) return "Einddatum is verplicht.";
                    if (new Date(value) <= new Date(startDateRef.current?.getValue())) return "Einddatum moet na de begindatum liggen.";
                    return "";
                  }}
                  placeholder="Kies een eind datum"
                  value={editMode ? formatDateForInput(previousData.vacationModeEnd) : ""}
                />
                <InputWithChecks
                  ref={reasonRef}
                  label="Vakantie Reden"
                  inputType="text"
                  validate={(value) => value ? "" : "Reden is verplicht."}
                  placeholder="Voer de reden van de vakantie in"
                  value={editMode ? previousData.vacationModeReason : ""}
                />
              </div>
              {isError && (
                <div className="c-r">
                  {error.info?.message ||
                    "Er is iets fout gegaan tijdens het verwerken van de vakantie modus."}
                </div>
              )}
              <div>
                {/* Toon de juiste knop op basis van editMode */}
                {editMode ? (
                  <SuccessButton onClick={handleFormSubmit} disabled={isPending}>
                    <div className="g-15-1fr">
                    <img src={editIconWhite} alt="bewerken" />
                      <span className="flex-0-5">
                        Vakantie Modus Bijwerken
                        {isPending && <LoadingIndicatorButton />}
                      </span>
                    </div>
                  </SuccessButton>
                ) : (
                  <PrimaryButton onClick={handleFormSubmit} disabled={isPending}>
                    <div className="g-15-1fr">
                  <img src={plusIconWhite} alt="vakantiemodus toevoegen" />

                      <span className="flex-0-5">
                        Vakantie Modus Toevoegen
                        {isPending && <LoadingIndicatorButton />}
                      </span>
                    </div>
                  </PrimaryButton>
                )}
              </div>
            </form>
          </BoxBorder>
        )}
      </Container>
    </section>
  );
};

export default VacationModeForm;
